import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Select,
  SelectItem,
  Skeleton,
  Table,
  TableColumn,
  TableHeader,
  type SharedSelection,
} from '@/components/ui';
import {
  DataTable,
  type Column,
  type Pagination,
} from '@/components/common/table';
import { StandardIcon } from '@/components/common/icon';
import { useApiWithAuth } from '@/hooks/api';
import type { ApiResponse } from '@/shared/types/api';
import { Icon } from '@iconify/react';

interface GroupTypeItem {
  pco_id: string;
  name: string;
}

interface GroupItem {
  pco_id: string;
  name: string;
  pco_group_type_id: string | null;
  memberships_count: number;
  contact_email: string | null;
  schedule: string | null;
}

interface GroupRow {
  id: string;
  data: GroupItem;
}

/** Client-side timeout so loading never hangs if the server doesn't respond */
const REQUEST_TIMEOUT_MS = 30_000;

function withRequestTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), ms),
    ),
  ]);
}

const GROUPS_COLUMNS = [
  { key: 'name', label: 'Group' },
  { key: 'type', label: 'Type' },
  { key: 'members', label: 'Members' },
  { key: 'email', label: 'Contact Email' },
  { key: 'schedule', label: 'Schedule' },
];

const SKELETON_ROW_COUNT = 5;

function GroupsTableSkeleton({ columns }: { columns: typeof GROUPS_COLUMNS }) {
  return (
    <Table isHeaderSticky className='max-h-[calc(100vh-220px)] text-gray-800'>
      <TableHeader columns={columns}>
        {(col) => <TableColumn key={col.key}>{col.label}</TableColumn>}
      </TableHeader>
      <tbody className='divide-y divide-slate-100'>
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
          <tr key={i}>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-48 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-24 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-12 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-36 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-28 rounded-md' />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export default function AccessGroups() {
  const { invoke, auth } = useApiWithAuth();
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;
  const [groupTypes, setGroupTypes] = useState<GroupTypeItem[]>([]);
  /** Raw key from Select (may be React-prefixed e.g. "1:$12898"); use selectedGroupTypeId for API */
  const [selectedGroupTypeKey, setSelectedGroupTypeKey] = useState('');
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const groupsRequestKey = useRef('');

  /** pco_id for API; strips React Select key prefix (e.g. "1:$12898" -> "12898") */
  const selectedGroupTypeId = useMemo(() => {
    const raw = selectedGroupTypeKey;
    if (!raw) {
      return '';
    }
    return raw.includes(':$') && raw.length > raw.indexOf(':$') + 1
      ? raw.split(':$').pop()!
      : raw;
  }, [selectedGroupTypeKey]);

  const groupTypeLookup = useMemo(() => {
    return groupTypes.reduce(
      (lookup, item) => {
        lookup[item.pco_id] = item.name;

        return lookup;
      },
      {} as Record<string, string>,
    );
  }, [groupTypes]);

  const columns = useMemo(
    (): Column<GroupRow>[] => [
      {
        key: 'name',
        label: 'Group',
        display: ({ item }) => (
          <span className='font-medium text-slate-800'>{item.data.name}</span>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        display: ({ item }) => {
          const typeId = item.data.pco_group_type_id;
          const label = typeId ? groupTypeLookup[typeId] : null;
          if (label === null || label === undefined) {
            return <></>;
          }
          return (
            <Chip className='rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs text-slate-700'>
              {label}
            </Chip>
          );
        },
      },
      {
        key: 'members',
        label: 'Members',
        display: ({ item }) => <span>{item.data.memberships_count}</span>,
      },
      {
        key: 'email',
        label: 'Contact Email',
        display: ({ item }) => <span>{item.data.contact_email || '-'}</span>,
      },
      {
        key: 'schedule',
        label: 'Schedule',
        display: ({ item }) => <span>{item.data.schedule || '-'}</span>,
      },
    ],
    [groupTypeLookup],
  );

  useEffect(() => {
    if (!auth.ready) {
      return undefined;
    }

    let cancelled = false;

    const loadGroupTypes = async () => {
      try {
        const response = await withRequestTimeout(
          invokeRef.current<ApiResponse<GroupTypeItem[]>>('/pco/groups/types'),
          REQUEST_TIMEOUT_MS,
        );
        if (cancelled) {
          return;
        }
        // Handle both successful JSON { data: T } and error/non-JSON response
        const list =
          response !== null &&
          response !== undefined &&
          typeof response === 'object' &&
          'data' in response &&
          Array.isArray((response as ApiResponse<GroupTypeItem[]>).data)
            ? (response as ApiResponse<GroupTypeItem[]>).data
            : [];
        setGroupTypes(list);
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading group types:', err);
        setGroupTypes([]);
      }
    };

    loadGroupTypes();

    return () => {
      cancelled = true;
    };
  }, [auth.ready]);

  useEffect(() => {
    if (!auth.ready) {
      setLoading(false);
      return undefined;
    }

    const requestKey = `${auth.firebaseUser?.uid ?? ''}|${selectedGroupTypeId}|${reloadToken}`;
    if (groupsRequestKey.current === requestKey) {
      // Already loading or loaded for this key (e.g. Strict Mode re-run skipped).
      // Ensure we don't leave loading true forever from a cancelled prior run.
      setLoading(false);
      return undefined;
    }

    groupsRequestKey.current = requestKey;
    let cancelled = false;

    const loadGroups = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        params.set('limit', '500');
        if (selectedGroupTypeId) {
          params.set('groupTypeId', selectedGroupTypeId);
        }

        const response = await withRequestTimeout(
          invokeRef.current<ApiResponse<GroupItem[]>>(
            `/pco/groups?${params.toString()}`,
          ),
          REQUEST_TIMEOUT_MS,
        );
        if (cancelled) {
          return;
        }

        if (response && typeof response === 'object' && 'data' in response) {
          setGroups(
            (response.data || []).map((item) => ({
              id: item.pco_id,
              data: item,
            })),
          );
        } else {
          setGroups([]);
        }
        setPage(1);
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading groups:', err);
        setGroups([]);
        setError(
          err instanceof Error && err.message === 'Request timed out'
            ? 'Request timed out. Try again or use Reload.'
            : 'Unable to load groups',
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadGroups();

    return () => {
      cancelled = true;
      // Reset so a re-run (e.g. React Strict Mode) or later mount will load again
      groupsRequestKey.current = '';
    };
  }, [auth.ready, auth.firebaseUser?.uid, selectedGroupTypeId, reloadToken]);

  const pagination: Pagination = useMemo(
    () => ({
      enabled: true,
      absolute: true,
      page,
      itemsPerPage,
      allowItemsPerPage: [10, 20, 50, 100],
      onPageChange: (nextPage: number) => setPage(nextPage),
      onItemsPerPageChange: (value: number) => {
        setItemsPerPage(value);
        setPage(1);
      },
    }),
    [page, itemsPerPage],
  );

  const onGroupTypeChange = (selection: SharedSelection) => {
    setSelectedGroupTypeKey(selection.currentKey || '');
  };

  const startIndex = (page - 1) * itemsPerPage;
  const displayedItems = useMemo(
    () => groups.slice(startIndex, startIndex + itemsPerPage),
    [groups, startIndex, itemsPerPage],
  );

  const showSkeleton = loading && groups.length === 0;

  return (
    <div>
      <div className='mb-2 flex flex-col gap-2 sm:flex-row sm:items-center'>
        <Select
          className='basis-1/3'
          aria-label='Filter groups by type'
          selectedKeys={new Set([selectedGroupTypeKey])}
          onSelectionChange={onGroupTypeChange}
          placeholder='All Group Types'
        >
          <SelectItem key=''>All Group Types</SelectItem>
          {groupTypes.map((item) => (
            <SelectItem key={item.pco_id}>{item.name}</SelectItem>
          ))}
        </Select>
        <Button
          variant='light'
          onPress={() => setReloadToken((token) => token + 1)}
          startContent={<StandardIcon id='mdi:reload' />}
        >
          Reload
        </Button>
      </div>
      {error ? (
        <Alert color='danger' className='mb-2'>
          {error}
        </Alert>
      ) : null}
      {showSkeleton ? (
        <GroupsTableSkeleton columns={GROUPS_COLUMNS} />
      ) : (
        <DataTable
          aria-label='Groups'
          columns={columns}
          items={displayedItems}
          count={groups.length}
          pagination={pagination}
          paginationVariant='numbered'
          loading={loading}
          className='text-gray-800'
          empty={{
            icon: 'mdi:account-group-outline',
            bottomLeftIcon: 'material-symbols:group-off-outline',
            title: 'No groups found',
            body: selectedGroupTypeId
              ? "We couldn't find any groups for the selected type. Try choosing a different group type or clear the filter."
              : "We couldn't find any groups. Try reloading or check your connection.",
            actions: (
              <>
                <Button
                  variant='flat'
                  onPress={() => setSelectedGroupTypeKey('')}
                  isDisabled={!selectedGroupTypeKey}
                >
                  Clear filter
                </Button>
                <Button
                  variant='flat'
                  onPress={() => setReloadToken((t) => t + 1)}
                  startContent={
                    <Icon icon='mdi:reload' width={18} height={18} />
                  }
                >
                  Reload
                </Button>
              </>
            ),
          }}
        />
      )}
    </div>
  );
}
